/**
 * useDemandHeatmap.ts · Polls `GET /api/mcp/heatmap?since_minutes=60`
 *
 * Phase 6 companion hook for the Demand tab. Same shape as useSimTraces so
 * the codebase has ONE polling idiom:
 *   - `enabled` prop pauses the interval when the Demand tab is inactive
 *     (we do not want to hammer the backend from Overview).
 *   - Fires once immediately on mount when enabled, then every 15s.
 *   - AbortController threaded through so a re-render during flight cancels
 *     the pending request cleanly.
 *
 * States: `idle` (pre-boot / pre-enable) · `loading` (fetch in flight) ·
 * `loaded` (last fetch settled with data) · `error` (last fetch failed).
 * Returns `{ cells, generatedAt, sinceMinutes, loading, error, refetch,
 * status }`. `refetch()` returns a promise that resolves once the fetch has
 * settled — useful for the "Simulate" button to await a heatmap refresh
 * before enabling the next click.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, getBaseUrl, isAbortError } from "../lib/api";
/** GET the heatmap directly — kept local so the hook is self-contained. */
async function fetchHeatmap(sinceMinutes, signal) {
    const url = `${getBaseUrl()}/api/mcp/heatmap?since_minutes=${encodeURIComponent(String(sinceMinutes))}`;
    const res = await fetch(url, {
        method: "GET",
        signal,
        headers: { Accept: "application/json" },
    });
    if (!res.ok) {
        const text = await safeText(res);
        throw new ApiError(`GET /api/mcp/heatmap failed with HTTP ${res.status}`, url, res.status, text);
    }
    return (await res.json());
}
async function safeText(res) {
    try {
        const t = await res.text();
        if (!t)
            return null;
        try {
            return JSON.parse(t);
        }
        catch {
            return t;
        }
    }
    catch {
        return null;
    }
}
/** Runtime coerce — defends against a partial backend deploy. */
function coerce(raw, fallbackSince) {
    if (!raw || typeof raw !== "object")
        return null;
    const o = raw;
    const cells = Array.isArray(o.cells)
        ? o.cells.filter((c) => c !== null &&
            typeof c === "object" &&
            typeof c.h3_cell === "string" &&
            typeof c.count === "number")
        : [];
    return {
        since_minutes: typeof o.since_minutes === "number" ? o.since_minutes : fallbackSince,
        generated_at: typeof o.generated_at === "string"
            ? o.generated_at
            : new Date().toISOString(),
        cells,
        simulated: Boolean(o.simulated),
    };
}
export function useDemandHeatmap(options = {}) {
    const { refetchIntervalMs = 15_000, sinceMinutes = 60, enabled = true, } = options;
    const [cells, setCells] = useState([]);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);
    const inflightRef = useRef(null);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
            if (timerRef.current !== null) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);
    const doFetch = useCallback(async () => {
        if (!mountedRef.current)
            return;
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        setStatus("loading");
        try {
            const raw = await fetchHeatmap(sinceMinutes, ac.signal);
            if (!mountedRef.current)
                return;
            const env = coerce(raw, sinceMinutes) ?? {
                since_minutes: sinceMinutes,
                generated_at: new Date().toISOString(),
                cells: [],
                simulated: true,
            };
            setCells(env.cells);
            setGeneratedAt(env.generated_at);
            setError(null);
            setStatus("loaded");
        }
        catch (err) {
            if (isAbortError(err) || !mountedRef.current)
                return;
            const msg = err instanceof ApiError
                ? `heatmap · ${err.short}`
                : err instanceof Error
                    ? `heatmap · ${err.message}`
                    : `heatmap · ${String(err)}`;
            setError(msg);
            setStatus("error");
        }
    }, [sinceMinutes]);
    const refetch = useCallback(async () => {
        // Coalesce concurrent refetch calls so a fast-clicking Simulate button
        // doesn't stack in-flight fetches.
        if (inflightRef.current)
            return inflightRef.current;
        const p = doFetch().finally(() => {
            inflightRef.current = null;
        });
        inflightRef.current = p;
        return p;
    }, [doFetch]);
    useEffect(() => {
        // Clear any prior timer when enabled/interval/window change.
        if (timerRef.current !== null) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (!enabled) {
            // When we go dormant we do NOT reset cells — the operator flipping
            // back to Demand should see the last known state instantly.
            abortRef.current?.abort();
            setStatus((prev) => (prev === "loading" ? "idle" : prev));
            return;
        }
        // Fire once immediately then set the interval.
        void refetch();
        if (refetchIntervalMs > 0) {
            timerRef.current = window.setInterval(() => {
                void refetch();
            }, refetchIntervalMs);
        }
        return () => {
            if (timerRef.current !== null) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [enabled, refetchIntervalMs, refetch]);
    return useMemo(() => ({
        cells,
        generatedAt,
        sinceMinutes,
        loading: status === "loading",
        error,
        status,
        refetch,
    }), [cells, generatedAt, sinceMinutes, status, error, refetch]);
}
